import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Spinner from "../../components/Spinner/Spinner";
import * as CONSTANT from "../../constants";
import { getUser } from "../../helpers/helperMethods";
import { Toast_Func } from "../../helpers/toast.helper";
import { NewPasswordFormFields } from "../../models/forms.model";
import { logout, useAuthState } from "../../context/UserAuthentication";
import styleClasses from "./accounts.module.scss";
import NewPasswordForm from "../../components/ResetPassword/NewPasswordForm";
import {useDispatch} from "react-redux";
import { customerBusinessService } from "../../services";
import AccountStatusModal from "./AccountStatusModal";
import { IAccountStatus } from "../../models/misc";
import {setShowCart} from "../../redux/slices/cartSlice";
import { unregisterPush } from "@braze/web-sdk";
import AccountBanner from "../../images/accountBanner.png"
import axiosWithInterceptor from "../../services/axiosWithInterceptor";
import { useIsDesignV2 } from "../../hooks/useIsDesignV2";

const NewPassword = () => {
  const history = useNavigate();
  const { dispatch } = useAuthState();
  const { search } = useLocation();
  const user = new URLSearchParams(search).get("user");
  const code = new URLSearchParams(search).get("code");
  const [isExpiredLink, setExpiredLink] = useState<boolean>(false);
  const [isVerifyingLink, setIsVerifyingLink] = useState<boolean>(true);
  const cartDispatch = useDispatch();
  const isDesignV2 = useIsDesignV2();
  const [accountStatusModal, setAccountStatusModal] = useState<IAccountStatus>({
    code: null,
    buttonText: null,
    isError: null,
    header: null,
    text: null,
    email: null,
    show: false,
  });
  const closeAccountStatusModal = () => {
    setAccountStatusModal({
      code: null,
      buttonText: null,
      isError: null,
      header: null,
      text: null,
      email: null,
      show: false,
    });
  };
  const updateAccountStatusModal = (
    header: string,
    text: string,
    code: number,
    buttonText: string,
    email: string,
    isError: boolean
  ) => {
    setAccountStatusModal({
      code,
      buttonText,
      isError,
      header,
      text,
      email,
      show: true,
    });
  };

  const handleFormSubmission = async (
    values: NewPasswordFormFields,
    { setSubmitting }: any
  ) => {
    try {
      const res = await customerBusinessService.changePassword({
        password: values.password,
        password_confirmation: values.confirm_password,
        user: user,
      })
      setSubmitting(false);
      showToast(res)
      logout(dispatch, cartDispatch);
      unregisterPush();
      history(CONSTANT.ROUTE_LOGIN);
    } catch (error) {
      /* Replace with original error once the API error response become similar for every endpoint */
      Toast_Func({
        status: false,
        message: error.response?.data?.data?.errors?.length ? error.response?.data?.data?.errors[0] : 'Cannot change the password',
      });
    }
  };

  const showToast = (res:any) => {
    if(res?.data?.errors){
      Toast_Func({
        status: false,
        message: res?.data?.errors?.length ? res?.data?.errors[0] : 'Cannot change the password',
      });
    }else{
      Toast_Func({
        status: true,
        message: res?.data?.message || 'Cannot change the password' ,
      });
    }
  }

  useEffect(() => {
    let authUser=getUser()
    const logOutUser = async () => {
      await axiosWithInterceptor.post(
        `${CONSTANT.CUSTOMER_MICRO_SERVICE}/logout`,
        {
          headers: {
            "Refresh-token": authUser?.refresh_token
          }
        }
      );
      logout(dispatch, cartDispatch);
      unregisterPush();
    }
    if(authUser.type !== CONSTANT.GUEST_USER && authUser?.authentication_token)
    {
      logOutUser()  
    }
    setIsVerifyingLink(true);
    cartDispatch(setShowCart(false))
    const verifyLink = async () => {
      try {
        const response = await customerBusinessService.verifyLink({
          code,
          user,
        });
        if (response.status === 200) {
          setExpiredLink(false);
        }
        setIsVerifyingLink(false);
      } catch (error) {
        setExpiredLink(true)
        const errorsObject = error?.response?.data?.errors;
        const errorCode = errorsObject?.code;
        if(errorCode)
          {
            const buttonText = CONSTANT.API_CODE_MESSAGES[errorCode];
            updateAccountStatusModal(
              errorsObject.header,
              errorsObject.text,
              errorCode,
              buttonText,
              errorsObject.email,
              true
            );
          }
        else {
            showToast(error.response);
            history(CONSTANT.ROUTE_LOGIN);
        }
        setIsVerifyingLink(false);
      }
    };
    verifyLink();
  }, []);

  if (isVerifyingLink) return <Spinner />;

  return (
    <>
      <AccountStatusModal
        accountStatusModal={accountStatusModal}
        updateAccountStatusModal={updateAccountStatusModal}
        closeModal={() => {
          closeAccountStatusModal()
          history(CONSTANT.ROUTE_LOGIN)
        }}
      />
      <Container fluid>
        <Row>
          <Col
            md="6"
            className={`${styleClasses.welcome_banner_wrap} p-0 d-none d-md-block`}
          >
            <div>
              <img
                src={AccountBanner}
                className="img-fluid"
                alt="account icon"
              />
            </div>
          </Col>
          <Col md="6" className="position-relative">
            <div className="text-start absolute-back-btn">
              <Link
                to={CONSTANT.ROUTE_WELCOME_SCREEN}
                className="btn btn-custom back-arrow-btn f-s14 px-0 mb-0"
              >
                Back
              </Link>
            </div>
            <div
              className={`${styleClasses.account_content_wrapper} new_form_design pt-4 pt-md-0 px-md-3 pb-5 pb-md-0`}
            >
              <div className="text-start d-md-none mb-3">
                <Link
                  to={CONSTANT.ROUTE_WELCOME_SCREEN}
                  className="btn btn-custom back-arrow-btn f-s14 px-0 mb-0"
                >
                  Back
                </Link>
              </div>
              {isExpiredLink ? (
                <>
                  <h1 className="font-Cls h-48 f-s32 f-sm-s22 text-center mb-4">
                    Link Expired!
                  </h1>
                  <p className="p-16 text-center">
                    This link can not be used to reset password any longer.
                  </p>
                  <hr className="my-4 d-md-none" />
                  <div className="max-content mx-auto pt-md-4">
                    <Link
                      to={CONSTANT.ROUTE_LOGIN}
                      className="text-decoration-none btn btn-large d-block"
                    >
                      SIGN IN
                    </Link>
                  </div>
                </>
              ) : (
                  <NewPasswordForm handleFormSubmission={handleFormSubmission}/>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <div>
</div>
    </>
  );
};

export default NewPassword;
